import React, { useState, useCallback, useEffect } from 'react';

import dashboardApi from 'services/apis/dashboard';

import { useSnackbar } from 'v3/components/Snackbar';
import moment from 'moment';
import Button from 'v3/components/Button';
import { useQuery } from 'hooks';
import { Table, Text, Link } from 'v3/components';
import { formatDateTime } from 'v3/utils/formatter';
import Card, { Number, Description, SeeAll } from './Card';

export default function DeclinedNegotiatorCustomerTravels({ dashboard, name, id }) {
	const [isFetching, setFetching] = useState(false);
	const [filter, setFilter] = useState({});
	const [currentPage, setCurrentPage] = useState({});
	const [pagination, setPagination] = useState({});
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState({});
	const [daysFromNow, setDaysFromNow] = useState(3);
	const snackbar = useSnackbar();
	const queryParams = useQuery();

	useEffect(() => {
		setDaysFromNow(moment(new Date()).diff(filter.start_date, 'days'));
	}, [filter.start_date]);

	function setQueryParams(filters) {
		const { tags = [], start_date, end_date } = filters;

		queryParams.delete('tags');

		if (tags) {
			tags.forEach(tag => {
				queryParams.append('tags', tag.id);
			});
		}

		if (start_date) {
			queryParams.set('start_date', start_date);
		}

		if (end_date) {
			queryParams.set('end_date', end_date);
		}

		queryParams.set('status', 2);

		if (tags?.length === 0) {
			queryParams.set('no-tags', true);
		} else {
			queryParams.delete('no-tags');
		}
	}
	const fetchTravels = useCallback(async (filters = {}) => {
		try {
			setLoading(true);
			const {
				tags = [],
				clients = [],
				negotiators = [],
				costCenters = [],
				recuseCostCenters = [],
				pcps = [],
				companies = [],
				page = 1,
				start_date,
				end_date,
			} = filters;

			if (page === 1) {
				setFetching(true);
			}

			setFilter(filters);
			setCurrentPage(+page);
			setQueryParams(filters);

			const response = await dashboardApi.get(
				`/loads-attendeds-negotiator-customer-declined`,
				{
					params: {
						tags: tags?.map(tag => tag.id),
						clients: clients?.map(client => client.id),
						start_date,
						negotiators: negotiators?.map(negotiator => negotiator?.id),
						costCenters: costCenters?.map(costCenter => costCenter?.id),
						recuseCostCenters: recuseCostCenters?.map(costCenters => costCenters?.id),
						pcps: pcps?.map(pcp => pcp?.id),
						companies: companies?.map(company => company?.id),
						end_date: end_date || undefined,
						page,
					},
				}
			);
			setPagination(response?.data?.last?.pagination);
			if (page === 1) {
				setData(response?.data);
			} else {
				setData(oldData => ({
					...oldData,
					last: {
						...oldData.last,
						data: [...oldData?.last?.data, ...response?.data?.last?.data],
					},
				}));
			}
		} catch (ex) {
			snackbar.show(<Text>Erro ao carregar últimas viagens</Text>, {
				type: 'error',
			});
		} finally {
			setLoading(false);
			setFetching(false);
		}
	}, []);

	return (
		<Card
			title="Viagens reprovadas Negociador"
			isLoading={isFetching}
			fetchFn={fetchTravels}
			onFiltersChanged={fetchTravels}
			dashboard={dashboard}
			excludeFilterByBranchOffice={true}
			excludeFilterByRecuseCostCenters={false}
			name={name}
			id={id}
			ListComponent={
				<>
					<Table striped>
						<thead>
							<tr>
								<th>
									<Text weight="500" color="dark">
										Centro de custo
									</Text>
								</th>
								<th>
									<Text weight="500" color="dark">
										Empresa
									</Text>
								</th>
								<th>
									<Text weight="500" color="dark">
										Tags da carga
									</Text>
								</th>
								<th>
									<Text weight="500" color="dark">
										Viagem
									</Text>
								</th>
								<th>
									<Text weight="500" color="dark">
										Data máxima agendada
									</Text>
								</th>
								<th>
									<Text weight="500" color="dark">
										Negociador
									</Text>
								</th>
								<th>
									<Text weight="500" color="dark">
										Origem
									</Text>
								</th>
								<th>
									<Text weight="500" color="dark">
										Destino
									</Text>
								</th>
								<th>
									<Text weight="500" color="dark">
										Cliente
									</Text>
								</th>
								<th>
									<Text weight="500" color="dark">
										Motorista
									</Text>
								</th>
								<th>
									<Text weight="500" color="dark">
										Veículo
									</Text>
								</th>
							</tr>
						</thead>
						<tbody>
							{data.last?.data?.map(travel => {
								const title = `Cliente ${travel.social_name} - ${travel.cgccpf}`;

								return (
									<tr>
										<td>
											<Text type="regular" color="gray">
												{travel.cost_center || '-'}
											</Text>
										</td>
										<td>
											<Text type="regular" color="gray">
												{travel?.company_name || '-'}
											</Text>
										</td>
										<td>
											<Text type="regular" color="gray">
												{travel.load_tags || '-'}
											</Text>
										</td>
										<td>
											<Link
												to={`/viagens/${travel.attended_id}`}
												title={title}
												target="_blank"
											>
												{travel.attended_id}
											</Link>
										</td>

										<td>
											<Text type="regular" color="gray">
												{formatDateTime(travel.scheduled_origin)}
											</Text>
										</td>
										<td>
											<Text type="regular" color="gray">
												{travel.user_created}
											</Text>
										</td>
										<td>
											<Text type="regular" color="gray" truncate>
												{travel.city_origin} - {travel.uf_origin}
											</Text>
										</td>
										<td>
											<Text type="regular" color="gray" truncate>
												{travel.city_destination} - {travel.uf_destination}
											</Text>
										</td>
										<td>
											<Text type="regular" color="gray" truncate>
												{travel.social_name}
											</Text>
										</td>
										<td>
											<Text type="regular" color="gray" truncate>
												{travel.driver_name}
											</Text>
										</td>
										<td>
											<Text type="regular" color="gray">
												{travel.plate}
											</Text>
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
					{pagination?.lastPage > 1 && pagination?.to < pagination?.total && (
						<div className="text-center mt-3 col-12">
							<Button
								className="mt-2 ml-auto align-item-center"
								onClick={() => {
									fetchTravels({
										page: currentPage + 1,
										tags: filter?.tags,
										clients: filter?.clients,
										start_date: filter?.start_date,
										end_date: filter?.end_date,
										costCenters: filter?.costCenters,
										recuseCostCenters: filter?.recuseCostCenters,
										pcps: filter?.pcps,
										companies: filter?.companies,
										negotiators: filter?.negotiators,
									});
								}}
								loading={loading}
							>
								<Text type="regular" color="white" truncate>
									Ver mais
								</Text>
							</Button>
						</div>
					)}

					{!!data?.total && (
						<SeeAll
							to={{
								pathname: '/viagens',
								search: queryParams.toString(),
							}}
						>
							ver todas as {data?.total} viagens
						</SeeAll>
					)}
				</>
			}
			NumericComponent={
				<div className="d-flex justify-content-center text-center flex-column">
					<Number color="error">{data?.total}</Number>
					<Description>
						reprovados
						{daysFromNow > 1 && daysFromNow < 61
							? ` nos últimos ${daysFromNow} dias`
							: ` nos últimos ${filter.start_date} dias`}
					</Description>
				</div>
			}
		/>
	);
}
